import { useState, useEffect, useMemo } from "react";
import { SegmentGroup, Text } from "@simplybusiness/mobius";
import { PaymentSummary } from "../../PaymentSummary/ExperimentLegalAgreements/PaymentSummary";
import { PaymentOptions } from "../../PaymentOptions";
import { JourneyContinuationNotice } from "../../JourneyContinuationNotice";
import { currencyFormatter } from "../../UtilsHelper";
import { useBreakpoint } from "@simplybusiness/mobius";
import { CombinedPaymentContext } from "../../CombinedPaymentContext";
import { SummaryWrapper } from "../../SummaryWrapper";
import { CallToAction } from "./CallToAction";
import {
  upfrontPayments,
  upfrontPaymentsTotal,
  monthlyPayments,
  firstMonthlyPaymentDate,
  monthlyPaymentSummary,
  productInfoTexts,
  selectProductsMarkedForPurchase,
  useTriggerEventTracker,
} from "../../helpers/PersistentPaymentSummaryHelper";
import type { PaymentTermsOption } from "../../PaymentTermsChecklist";
import { PaymentTermsChecklist } from "../../PaymentTermsChecklist";

export const PersistentCartPaymentOptions = ({
  businessInformation,
  productsInformation,
  paymentBreakdown,
  currency,
  totalPaymentAmount,
  requestPath,
  csrfToken,
  journeyToken,
  continuationProduct = "",
  multiProductText,
  onPurchaseCartDigestUpdate = _newValue => {},
  onPaymentOptionsUpdate = _productInformation => {},
  onCallToActionClick = _newValue => {},
  onPayTodayAmountUpdate = _payTodayAmount => {},
  showPaymentSummaryOnly = false,
  enableCheckoutFormSubmission = true,
  enablePaymentOptionsServerUpdate = false,
  combinedPaymentPage = false,
  isExternalCarrier = false,
  hasMonthlyFees = false,
  cardFeeDisclosureText = "",
  poweredByStripeLogo = "",
  agreementChecklist = [],
  eventTrackerCallback,
  legalAgreementButtonsNotCheckedCallback,
  firePayButtonClickedEvent,
  checkboxLinkClickedCallback,
}: PersistentCartPaymentOptionsProps) => {
  const [productsInfo, setProductsInfo] = useState(productsInformation);
  const [dynamicPaymentBreakdown, setDynamicPaymentBreakdown] =
    useState(paymentBreakdown);
  const [totalPayment, setTotalPayment] = useState(totalPaymentAmount);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const productsMarkedForPurchase =
    selectProductsMarkedForPurchase(productsInfo);
  const breakpointSize = useBreakpoint([
    { size: "sm", value: 639 },
    { size: "lg", value: 640 },
  ]).breakpoint.size;
  const [eventDetails, setEventDetails] = useState<{
    eventName: string | null;
    args: string[];
  }>({ eventName: null, args: [] });
  const [checkedAgreements, setCheckedAgreements] = useState<string[]>([]);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const errorMessage =
    "Please review and confirm these statements to proceed to payment";

  const allAccepted = agreementChecklist.every(opt =>
    checkedAgreements.includes(opt.value),
  );
  const showError = hasAttemptedSubmit && !allAccepted;

  useTriggerEventTracker(eventDetails.eventName, ...eventDetails.args);

  const firePaymentOptionChangeEvent = (
    cartItemReference: string,
    newPaymentPeriod: string,
  ) => {
    const currentProduct = productsInfo.find(
      product => product.cartItemReference === cartItemReference,
    );

    try {
      setEventDetails({
        eventName: "payment_option_changed",
        args: [
          newPaymentPeriod,
          ...(currentProduct ? currentProduct.coverNames : [""]),
        ],
      });
    } catch (e) {
      console.error("Error in TriggerEventTracker", e);
    }
  };

  const fireCheckoutButtonClickedEvent = () => {
    productsInfo.forEach(product => {
      setEventDetails({
        eventName: "checkout_button_clicked",
        args: [
          product.selectedPaymentOption,
          ...(product ? product.coverNames : [""]),
        ],
      });
    });
  };

  const handleUpdateItem = (response: {
    productsInformation: ProductInformation[];
    totalPaymentAmount: number;
    paymentBreakdown: PaymentBreakdown;
    purchase_cart_digest: string;
  }) => {
    setProductsInfo(response.productsInformation);
    setTotalPayment(response.totalPaymentAmount);
    setDynamicPaymentBreakdown(response.paymentBreakdown);
    onPaymentOptionsUpdate(response.productsInformation);
    onPurchaseCartDigestUpdate(response.purchase_cart_digest);
  };

  const handlePaymentOptionsClientSideUpdate = (
    cartItemReference: string,
    newPaymentPeriod: string,
  ) => {
    firePaymentOptionChangeEvent(cartItemReference, newPaymentPeriod);
    setProductsInfo(prevState =>
      prevState.map(productInfo =>
        productInfo.cartItemReference === cartItemReference
          ? { ...productInfo, selectedPaymentOption: newPaymentPeriod }
          : productInfo,
      ),
    );
  };

  const upfrontPaymentsTotalValue = useMemo(() => {
    return upfrontPaymentsTotal(productsMarkedForPurchase, currency);
  }, [productsMarkedForPurchase, currency]);

  const buttonText = `Pay ${upfrontPaymentsTotalValue || ""}`;

  useEffect(() => {
    onPayTodayAmountUpdate(buttonText);
  }, [buttonText, onPayTodayAmountUpdate]);

  const upfrontPaymentBreakdown = () => {
    if (!enablePaymentOptionsServerUpdate) {
      return upfrontPayments(productsMarkedForPurchase, currency);
    }

    return dynamicPaymentBreakdown.downpayment;
  };

  const monthlyPaymentBreakdown = () => {
    if (!enablePaymentOptionsServerUpdate) {
      return monthlyPayments(
        productsMarkedForPurchase,
        currency,
        hasMonthlyFees,
      );
    }

    return dynamicPaymentBreakdown.installments;
  };

  const buildFormAttributes = () => {
    if (!enableCheckoutFormSubmission) return {};

    return {
      action: requestPath,
      method: "POST",
      onSubmit: () => setIsFormSubmitting(true),
    };
  };

  const nonRefundableTexts = productInfoTexts(
    productsMarkedForPurchase,
    "nonRefundableText",
  );
  const surchargeTexts = productInfoTexts(
    productsMarkedForPurchase,
    "surchargeText",
  );

  return (
    <CombinedPaymentContext.Provider value={combinedPaymentPage}>
      <form
        data-testid="checkout-form"
        id="checkout-form"
        {...buildFormAttributes()}
      >
        <input
          className="hidden"
          name="authenticity_token"
          type="hidden"
          data-testid="authenticity-token"
          value={csrfToken}
        />
        <input
          className="hidden"
          name="journey_token"
          type="hidden"
          value={journeyToken}
          data-testid="journey-token"
        />
        <div
          className={`mobius-payment-summary__outer-grid ${combinedPaymentPage ? "combined-payment-page" : undefined} ${showPaymentSummaryOnly ? "show-payment-summary-only" : undefined}`}
        >
          {!showPaymentSummaryOnly && (
            <>
              <div
                className={`mobius-payment-summary__product-payment-options ${combinedPaymentPage ? "combined-styles" : undefined}`}
              >
                {productsInfo.map((productInfo: ProductInformation) => (
                  <SegmentGroup
                    key={productInfo.cartItemReference}
                    className={
                      !productInfo.promptInformation?.promptCustomer
                        ? "product-group"
                        : "product-group non-purchasable-product"
                    }
                    data-testid={productInfo.cartItemReference}
                    horizontal={breakpointSize == "lg"}
                  >
                    <SummaryWrapper
                      productName=""
                      updateApplicationDashboard={(response: unknown) => {
                        console.log("updateApplicationDashboard");
                        console.log(response);
                      }}
                      productSummary={
                        productInfo.productSummary.coverInformation
                      }
                      policyStartDate={
                        productInfo.productSummary.policyStartDate
                      }
                      insurerLogo={productInfo.productSummary.insurerLogo}
                      hasPrimaryCover={
                        productInfo.productSummary.hasPrimaryCover
                      }
                      hasSharedLimits={
                        productInfo.productSummary.hasSharedLimits
                      }
                      mobileView
                      expandCover={false}
                      editURL={productInfo.editUrl}
                      markedForPurchase={productInfo.markedForPurchase}
                      onUpdateItem={handleUpdateItem}
                      cartItemReference={productInfo.cartItemReference}
                      promptInformation={productInfo.promptInformation}
                      eligibleForRemoval={productInfo.eligibleForRemoval}
                      editModalDescription={productInfo.editModalDescription}
                    />
                    <PaymentOptions
                      annual={productInfo.availablePaymentOptions.annual}
                      monthly={productInfo.availablePaymentOptions.monthly}
                      currency={currency}
                      selectedOption={productInfo.selectedPaymentOption}
                      cartItemReference={productInfo.cartItemReference}
                      fireEvent={firePaymentOptionChangeEvent}
                      markedForPurchase={productInfo.markedForPurchase}
                      promptInformation={productInfo.promptInformation}
                      onUpdateItem={handleUpdateItem}
                      enablePaymentOptionsServerUpdate={
                        enablePaymentOptionsServerUpdate
                      }
                      hasMonthlyFees={hasMonthlyFees}
                      onPaymentOptionsClientUpdate={
                        handlePaymentOptionsClientSideUpdate
                      }
                    />
                  </SegmentGroup>
                ))}
                <div className="mobius-payment-summary__checklist-container">
                  <PaymentTermsChecklist
                    id="checklist"
                    options={agreementChecklist}
                    value={checkedAgreements}
                    onChange={setCheckedAgreements}
                    isInvalid={showError}
                    errorMessage={showError ? errorMessage : undefined}
                    checkboxLinkClickEvent={checkboxLinkClickedCallback}
                  />
                </div>
              </div>
              {continuationProduct.length > 0 && (
                <JourneyContinuationNotice
                  continuationProduct={continuationProduct}
                />
              )}
            </>
          )}
          <div className="mobius-payment-summary__payment-summary-box">
            <div className="payment-summary">
              <PaymentSummary
                primaryTrade={businessInformation.primaryTrade}
                businessName={businessInformation.businessName}
                upfrontPayments={upfrontPaymentBreakdown()}
                upfrontPaymentsTotal={upfrontPaymentsTotalValue}
                monthlyPayments={monthlyPaymentBreakdown()}
                monthlyPaymentSummary={monthlyPaymentSummary(
                  productsMarkedForPurchase,
                  currency,
                )}
                firstMonthlyPaymentDate={firstMonthlyPaymentDate(
                  productsMarkedForPurchase,
                  currency,
                )}
                totalPaymentAmount={currencyFormatter(currency).format(
                  totalPayment,
                )}
                isExternalCarrier={isExternalCarrier}
                hasMonthlyFees={hasMonthlyFees}
              />
              <CallToAction
                eventTrackerCallback={eventTrackerCallback}
                enableCheckoutFormSubmission={enableCheckoutFormSubmission}
                isFormSubmitting={isFormSubmitting}
                fireCheckoutButtonClickedEvent={fireCheckoutButtonClickedEvent}
                onCallToActionClick={onCallToActionClick}
                buttonText={buttonText}
                poweredByStripeLogo={poweredByStripeLogo}
                legalAgreementButtonsNotCheckedCallback={
                  legalAgreementButtonsNotCheckedCallback
                }
                combinedPaymentPage={combinedPaymentPage}
                firePayButtonClickedEvent={firePayButtonClickedEvent}
                checkedAgreements={checkedAgreements}
                agreementOptions={agreementChecklist}
                setHasAttemptedSubmit={setHasAttemptedSubmit}
              />
            </div>
          </div>
        </div>
        <Text variant="legal">{multiProductText}</Text>
        {nonRefundableTexts.map((text, index) => (
          <Text variant="legal" key={["non-refundable-text", index].join("-")}>
            {text}
          </Text>
        ))}

        {surchargeTexts.map((text, index) => (
          <Text variant="legal" key={["surcharge-text", index].join("-")}>
            {text}
          </Text>
        ))}

        <Text variant="legal">{cardFeeDisclosureText}</Text>
      </form>
    </CombinedPaymentContext.Provider>
  );
};

interface BusinessInformation {
  primaryTrade: string;
  businessName: string;
}

interface InstallmentSchedule {
  month: string;
  year: string;
  amount: number;
}

interface Monthly {
  downpaymentAmount: number;
  installmentAmount?: number;
  firstInstallmentDate: string;
  installmentsSchedule: InstallmentSchedule[];
  totalPayable?: number | null;
}

export interface AvailablePaymentOptions {
  annual: {
    amount: number;
  };
  monthly: Monthly;
}

export interface CoverInformation {
  displayLimits: {
    label: string;
    value: string;
  }[];
  displayPremium: string;
  displayExcessTitle: string | null;
  displayExcessAmount: string | null;
  displaySpecificExcesses: {};
  covered: boolean;
  optional: boolean;
}

export interface ProductSummary {
  coverInformation: Record<string, CoverInformation>;
  insurerLogo: string;
  policyStartDate?: string;
  hasPrimaryCover?: boolean;
  hasSharedLimits?: boolean;
}

export interface ProductInformation {
  markedForPurchase: boolean;
  cartItemReference: string;
  coverNames: string[];
  selectedPaymentOption: string;
  availablePaymentOptions: AvailablePaymentOptions;
  nonRefundableText?: string;
  surchargeText?: string;
  productSummary: ProductSummary;
  promptInformation: {
    promptCustomer: boolean;
    message: string;
    buttonText: string;
    actionUrl: string;
    type: string;
  };
  editUrl?: string;
  eligibleForRemoval?: boolean;
  editModalDescription?: string;
}

export interface PaymentBreakdown {
  downpayment: {
    labels: string[];
    value: string;
  }[];
  installments: {
    labels: string[];
    value: string;
  }[];
}

export interface PersistentCartPaymentOptionsProps {
  businessInformation: BusinessInformation;
  productsInformation: ProductInformation[];
  paymentBreakdown: PaymentBreakdown;
  onPurchaseCartDigestUpdate?: (newPurchaseCartDigestValue: string) => void;
  onPaymentOptionsUpdate?: (productsInformation: ProductInformation[]) => void;
  onCallToActionClick?: (drawerOpen: boolean) => void;
  onPayTodayAmountUpdate?: (payTodayAmount: string) => void;
  currency: string;
  totalPaymentAmount: number;
  showPaymentSummaryOnly?: boolean;
  requestPath: string;
  csrfToken: string;
  journeyToken: string;
  continuationProduct: string;
  multiProductText?: string;
  enableCheckoutFormSubmission: boolean;
  enablePaymentOptionsServerUpdate: boolean;
  agreementChecklist?: PaymentTermsOption[];
  combinedPaymentPage: boolean;
  isExternalCarrier?: boolean;
  hasMonthlyFees?: boolean;
  cardFeeDisclosureText?: string;
  poweredByStripeLogo: string;
  eventTrackerCallback: () => void;
  legalAgreementButtonsNotCheckedCallback?: () => void;
  firePayButtonClickedEvent?: () => void;
  legalAgreementsExperimentEnabled?: boolean;
  checkboxLinkClickedCallback?: (checkboxTitle: string) => void;
}
